import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight, Check } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { SectionLabel, Reveal, CodeBlock } from "@/components/platform/primitives";
import ArchitectureDiagram from "@/components/platform/ArchitectureDiagram";
import ProductFaq from "@/components/platform/ProductFaq";
import { getProductBySlug, getRelatedProducts } from "@/data/platform";
import { setMetaDescription } from "@/lib/seo";

export default function ProductPage() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} · CashFUX Plattform`;
      setMetaDescription(product.hero.subtitle);
    }
    return () => {
      document.title = "CashFUX";
    };
  }, [product]);

  if (!product) {
    return (
      <div className="relative min-h-screen bg-[#05060a] text-white font-body">
        <Header />
        <div className="mx-auto flex min-h-[70vh] max-w-[1400px] flex-col items-center justify-center px-6 text-center">
          <span className="font-mono text-sm uppercase tracking-[0.25em] text-[#e01e37]">404</span>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight">Seite nicht gefunden</h1>
          <p className="mt-4 text-white/55">Dieses Plattform-Modul existiert nicht.</p>
          <Link
            to="/plattform"
            data-testid="notfound-back"
            className="mt-8 inline-flex items-center gap-2 bg-[#e01e37] px-6 py-3 text-sm font-medium text-white hover:bg-[#c91830] transition-colors"
          >
            Zur Plattform-Übersicht <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = getRelatedProducts(product);

  return (
    <div className="relative min-h-screen bg-[#05060a] text-white font-body antialiased" data-testid="product-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden pt-28 lg:pt-36 pb-16 lg:pb-24" data-testid="product-hero">
        <div className="absolute inset-0 grid-bg mask-radial" />
        <div className="absolute -top-32 left-1/4 h-[440px] w-[440px] rounded-full bg-[#e01e37]/10 blur-[130px]" />

        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          {/* Breadcrumb */}
          <nav className="mb-10 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Start</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/plattform" className="hover:text-white transition-colors">Plattform</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#e01e37]">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <SectionLabel>{product.overline}</SectionLabel>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mt-6 font-display font-extrabold tracking-tight text-white leading-[1.0] text-4xl sm:text-5xl lg:text-[3.75rem]"
              >
                {product.hero.title}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="mt-7 max-w-xl text-base lg:text-lg leading-relaxed text-white/60"
              >
                {product.hero.subtitle}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.28 }}
                className="mt-9 flex flex-col sm:flex-row gap-4"
              >
                <a
                  href="#kontakt-cta"
                  data-testid="product-hero-primary-cta"
                  className="group inline-flex items-center justify-center gap-2 bg-[#e01e37] px-7 py-4 text-sm font-medium text-white transition-[background-color] duration-300 hover:bg-[#c91830] accent-glow"
                >
                  Demo anfragen
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href="#product-api"
                  data-testid="product-hero-secondary-cta"
                  className="group inline-flex items-center justify-center gap-2 border border-white/20 px-7 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5"
                >
                  API ansehen
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.div>
            </div>

            {/* Stats panel */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="border border-white/10 bg-[#0a0c12]/60 backdrop-blur-sm">
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center border border-[#e01e37]/40 text-[#e01e37]">
                      <product.icon className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                    <span className="font-display font-bold tracking-tight">{product.name}</span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Modul</span>
                </div>
                <div className="grid grid-cols-3 gap-px bg-white/10">
                  {product.stats.map((s, i) => (
                    <div key={i} className="bg-[#0a0c12] px-2 py-5 sm:px-4 sm:py-6 text-center">
                      <div className="font-display text-base sm:text-xl lg:text-2xl font-extrabold tracking-tight leading-tight text-white break-words">{s.value}</div>
                      <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.1em] text-white/40 leading-tight">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative py-14 sm:py-20 lg:py-28" data-testid="product-features">
        <div className="absolute inset-0 grid-bg-fine opacity-40" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <SectionLabel>Funktionen</SectionLabel>
            <h2 className="mt-6 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Was {product.name} im Kern ausmacht.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {product.features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group relative bg-[#05060a] p-8 lg:p-10 transition-colors duration-500 hover:bg-[#0a0c12]"
                data-testid={`product-feature-${i}`}
              >
                <span className="absolute left-0 top-0 h-0.5 w-0 bg-[#e01e37] transition-[width] duration-500 group-hover:w-full" />
                <div className="flex h-12 w-12 items-center justify-center border border-white/10 text-white/80 transition-colors duration-300 group-hover:border-[#e01e37]/50 group-hover:text-[#e01e37]">
                  <f.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-7 font-display text-xl lg:text-2xl font-bold tracking-tight">{f.title}</h3>
                <p className="mt-3 text-sm lg:text-[15px] leading-relaxed text-white/50">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHITECTURE */}
      <section className="relative border-y border-white/10 bg-[#0a0c12] py-14 sm:py-20 lg:py-28" data-testid="product-architecture">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <SectionLabel>Architektur</SectionLabel>
                <h2 className="mt-6 font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-[1.08]">
                  So fügt sich {product.name} in die Plattform.
                </h2>
                <p className="mt-5 max-w-md text-base leading-relaxed text-white/55">
                  Jedes Modul sitzt auf demselben Ledger Core. Ein Datenmodell,
                  eine Wahrheit, ein Integrationspunkt.
                </p>
              </div>
            </div>
            <div className="lg:col-span-8">
              <ArchitectureDiagram layers={product.architecture.layers} caption={product.architecture.caption} />
            </div>
          </div>
        </div>
      </section>

      {/* API */}
      <section id="product-api" className="relative py-14 sm:py-20 lg:py-28" data-testid="product-api">
        <div className="absolute inset-0 grid-bg-fine opacity-30" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <SectionLabel>API-Highlights</SectionLabel>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.04]">
                Integration in Stunden, nicht Monaten.
              </h2>
              <ul className="mt-9 space-y-4">
                {product.api.highlights.map((h, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-[#e01e37]/40 text-[#e01e37]">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm lg:text-base text-white/70">{h}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <Reveal delay={0.1} className="lg:col-span-7">
              <CodeBlock
                filename={`cashfux.${product.slug}.ts`}
                method={product.api.method}
                endpoint={product.api.endpoint}
                code={product.api.code}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="relative border-t border-white/10 py-14 sm:py-20 lg:py-28" data-testid="product-usecases">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <SectionLabel>Anwendungsfälle</SectionLabel>
            <h2 className="mt-6 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Wofür Teams {product.name} einsetzen.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {product.useCases.map((u, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-[#05060a] p-8 lg:p-10 transition-colors duration-500 hover:bg-[#0a0c12]"
                data-testid={`product-usecase-${i}`}
              >
                <span className="font-mono text-2xl text-white/20 transition-colors duration-500 group-hover:text-[#e01e37]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-6 font-display text-xl lg:text-2xl font-bold tracking-tight">{u.title}</h3>
                <p className="mt-3 text-sm lg:text-[15px] leading-relaxed text-white/50">{u.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative border-t border-white/10 bg-[#0a0c12] py-14 sm:py-20 lg:py-28" data-testid="product-faq">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <SectionLabel>FAQ</SectionLabel>
              <h2 className="mt-6 font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-[1.08]">
                Häufige Fragen zu {product.name}.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <ProductFaq items={product.faq} />
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="relative border-t border-white/10 py-14 sm:py-20 lg:py-24" data-testid="product-related">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <SectionLabel>Verwandte Module</SectionLabel>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/plattform/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group flex items-center justify-between bg-[#05060a] p-7 transition-colors duration-500 hover:bg-[#0a0c12]"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center border border-white/10 text-white/80 transition-colors duration-300 group-hover:border-[#e01e37]/50 group-hover:text-[#e01e37]">
                      <r.icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <span className="font-display text-lg font-bold tracking-tight">{r.name}</span>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-white/20 transition-all duration-300 group-hover:text-[#e01e37] group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section id="kontakt-cta" className="relative overflow-hidden py-16 sm:py-24 lg:py-32" data-testid="product-cta">
        <div className="absolute inset-0 grid-bg mask-radial opacity-60" />
        <div className="absolute left-1/2 top-1/2 h-[420px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#e01e37]/10 blur-[140px]" />
        <div className="relative mx-auto max-w-[900px] px-6 lg:px-10 text-center">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-display text-3xl sm:text-4xl lg:text-[3.25rem] font-extrabold tracking-tight leading-[1.04] text-balance">
              {product.name} in Ihr Produkt integrieren.
            </h2>
            <p className="mx-auto mt-6 max-w-lg text-base lg:text-lg text-white/55 leading-relaxed">
              Sprechen Sie mit unserem Team über Architektur, Compliance und den
              Weg zum Go-Live.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:kontakt@cashfux.com"
                data-testid="product-cta-primary"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#e01e37] px-8 py-4 text-sm font-medium text-white transition-[background-color] duration-300 hover:bg-[#c91830] accent-glow"
              >
                Plattform entdecken
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link
                to="/plattform"
                data-testid="product-cta-secondary"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 border border-white/20 px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5"
              >
                Alle Module
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
